import { useEffect, useState } from "react";

import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  Heading,
  Image,
  useColorModeValue,
} from "@vygruppen/spor-react";

import { usePushSubscriptionState } from "~/features/push-notifications/PushSubscriptionContext";
import { useNativeSubscription } from "~/features/push-notifications/use-native-subscription";
import { useSubscribe } from "~/features/push-notifications/use-subscribe";
import { isInstalled } from "~/features/pwa/utils";
import { useRequestDeviceToken } from "~/features/twa/use-request-device-token";

type Props = {
  employeeId: string;
};

export const PushNotificationsModal: React.FC<Props> = ({ employeeId }) => {
  const { status, defer } = usePushSubscriptionState();
  const subscribe = useSubscribe();
  const [isOpen, setIsOpen] = useState(false);

  const pushNotificationsIllustration = useColorModeValue(
    "/illustrations/push-notifications.svg",
    "/illustrations/push-notifications-dark.svg",
  );

  useNativeSubscription(employeeId);
  useRequestDeviceToken();

  useEffect(() => {
    if (
      status === "deferred" ||
      status === "subscribed" ||
      status === "denied" ||
      status === "unsupported"
    ) {
      setIsOpen(false);
    } else if (isInstalled() && status === "default") {
      setIsOpen(true);
    } else if (status === "granted") {
      subscribe.mutate({ employeeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleClose = () => {
    defer();
    setIsOpen(false);
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(event) => {
        if (!event.open) {
          handleClose();
        }
      }}
    >
      <DialogBackdrop />
      <DialogContent alignItems="center" marginX={3}>
        <DialogHeader
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          <Image src={pushNotificationsIllustration} alt="" width={11} />
          <Heading as="h1">Push-varslinger</Heading>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody textAlign="center">
          Du må tillate push-varslinger for å kunne motta viktige beskjeder.
          Vennligst trykk "Tillat" på neste bilde.
        </DialogBody>
        {status === "default" && (
          <DialogFooter gap={2}>
            <Button onClick={handleClose} variant="tertiary">
              Avbryt
            </Button>
            <Button
              onClick={() => subscribe.mutate({ employeeId })}
              loading={subscribe.isPending}
            >
              Neste
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </DialogRoot>
  );
};
